import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, CloudOff, Check, Loader2, Lock, Unlock, Users, BookMarked, MonitorPlay, FileDown, Maximize, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDeck } from './store';
import { SLIDES, TAGS, coverage } from './slides';
import { UnlockDialog, TeamDialog, LibrarySheet } from './Dialogs';
import { ChecklistDialog, useChecklist } from './Checklist';
import { ClipboardCheck } from 'lucide-react';

function ChecklistButton({ onClick }: { onClick: () => void }) {
  const { errors } = useChecklist();
  return (
    <button className={btn} onClick={onClick} data-testid="checklist-btn" title="Vérification avant l'oral">
      <ClipboardCheck size={16} />
      {errors > 0 ? <span className="rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white" data-testid="checklist-badge">{errors}</span> : <span className="h-2 w-2 rounded-full bg-emerald-400" />}
    </button>
  );
}

const btn = 'flex h-10 items-center gap-2 rounded-full px-3 font-ui text-[13px] font-medium text-white/85 hover:bg-white/10 hover:text-white active:scale-95 transition-[background-color,transform,color]';

function SaveStatus() {
  const { status, canEdit } = useDeck();
  if (!canEdit) return <span className="flex items-center gap-1.5 px-2 font-ui text-[12px] text-white/50" data-testid="readonly-badge"><Lock size={13} /> Lecture seule</span>;
  const map = {
    saved: [<Check size={13} key="i" />, 'Enregistré', 'text-emerald-400'],
    saving: [<Loader2 size={13} className="animate-spin" key="i" />, 'Enregistrement…', 'text-white/70'],
    offline: [<CloudOff size={13} key="i" />, 'Hors ligne — en attente', 'text-orange-400'],
  } as const;
  const [icon, label, tone] = map[status];
  return <span className={`flex items-center gap-1.5 px-2 font-ui text-[12px] ${tone}`} data-testid="save-status">{icon}{label}</span>;
}

function Coverage() {
  const { get, canEdit } = useDeck();
  if (!canEdit) return null;
  const c = coverage(get);
  return (
    <div className="flex items-center gap-1 px-1" data-testid="coverage-indicator" title="Sous-parties étiquetées par volet de la problématique">
      {TAGS.map((t) => (
        <span key={t.id} data-testid={`coverage-${t.id}`} className={`rounded-full px-2 py-0.5 font-ui text-[11px] font-semibold ${c[t.id] ? 'bg-white/10 text-white/80' : 'bg-red-600 text-white'}`}>
          {t.label} {c[t.id]}
        </span>
      ))}
    </div>
  );
}

interface Props { index: number; go: (n: number) => void; fullscreen: () => void; presenter: () => void }

export function Toolbar({ index, go, fullscreen, presenter }: Props) {
  const { canEdit, author, lock, slug } = useDeck();
  const [visible, setVisible] = useState(true);
  const [dlg, setDlg] = useState<null | 'unlock' | 'team' | 'lib' | 'check'>(null);

  useEffect(() => {
    let t: any;
    const show = () => { setVisible(true); clearTimeout(t); t = setTimeout(() => setVisible(false), 2800); };
    show();
    window.addEventListener('mousemove', show);
    return () => { window.removeEventListener('mousemove', show); clearTimeout(t); };
  }, []);

  const share = () => { navigator.clipboard?.writeText(`${window.location.origin}/p/${slug}`); toast.success('Lien copié. Donnez aussi le code de groupe à vos camarades.'); };
  const d = SLIDES[index];

  return (
    <>
      <div className={`no-print glass-dark fixed bottom-5 left-5 z-50 flex max-w-[calc(100vw-40px)] flex-wrap items-center gap-1 rounded-full border border-white/10 p-1.5 shadow-2xl transition-[opacity,transform] duration-300 ${visible || dlg ? 'opacity-100 translate-y-0' : 'pointer-events-none translate-y-4 opacity-0'}`} data-testid="deck-toolbar">
        <button className={btn} onClick={() => go(index - 1)} data-testid="slide-prev-btn" aria-label="Précédente"><ChevronLeft size={18} /></button>
        <span className="min-w-[150px] px-1 font-app text-[13px] text-white" data-testid="slide-counter">
          <b>{String(index + 1).padStart(2, '0')}</b><span className="text-white/40"> / {SLIDES.length}</span> <span className="text-white/60">· {d.label}</span>
        </span>
        <button className={btn} onClick={() => go(index + 1)} data-testid="slide-next-btn" aria-label="Suivante"><ChevronRight size={18} /></button>
        <span className="mx-1 h-6 w-px bg-white/15" />
        <SaveStatus />
        <Coverage />
        <span className="mx-1 h-6 w-px bg-white/15" />
        {canEdit ? (
          <button className={btn} onClick={lock} data-testid="lock-btn" title="Verrouiller sur cet ordinateur"><Unlock size={16} /> {author || 'Édition'}</button>
        ) : (
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => setDlg('unlock')} data-testid="unlock-btn"><Lock size={16} /> Modifier</button>
        )}
        {canEdit && <button className={btn} onClick={() => setDlg('team')} data-testid="team-btn" title="Prénoms et rôles"><Users size={16} /></button>}
        {canEdit && <ChecklistButton onClick={() => setDlg('check')} />}
        <button className={btn} onClick={() => setDlg('lib')} data-testid="library-btn" title="Sources sûres"><BookMarked size={16} /></button>
        <button className={btn} onClick={share} data-testid="share-btn" title="Copier le lien"><Share2 size={16} /></button>
        <button className={btn} onClick={presenter} data-testid="presenter-btn" title="Mode présentateur (B)"><MonitorPlay size={16} /></button>
        <button className={btn} onClick={() => window.open(`/p/${slug}/print`, '_blank')} data-testid="pdf-btn" title="Export PDF"><FileDown size={16} /></button>
        <button className={btn} onClick={fullscreen} data-testid="fullscreen-btn" title="Plein écran (F)"><Maximize size={16} /></button>
      </div>
      <UnlockDialog open={dlg === 'unlock'} onClose={() => setDlg(null)} />
      <TeamDialog open={dlg === 'team'} onClose={() => setDlg(null)} />
      <LibrarySheet open={dlg === 'lib'} onClose={() => setDlg(null)} />
      {canEdit && <ChecklistDialog open={dlg === 'check'} onClose={() => setDlg(null)} go={go} />}
    </>
  );
}
